import { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { ScreenQuad } from '@react-three/drei';
import { EffectComposer, Bloom, ChromaticAberration, Noise, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import type { AudioProcessor, AudioFeatures } from '@/lib/audioProcessor';
import {
  CORTICAL_FIELD, SYMMETRY_FOLD, HYPERBOLIC_WARP, NOISE_FBM,
  SDF_LIB, AUDIO_SAMPLE, COLOR_LIB,
} from '@/lib/psychedelic/shaderLib';
import type { PsyState } from '@/lib/psychedelic/constants';
import { PALETTES } from '@/lib/psychedelic/constants';
import { CanvasErrorBoundary } from '@/components/CanvasErrorBoundary';

/**
 * Psychedelic phenomenology visualizer — v3 (correct uniform lifecycle).
 *
 * Critical fix: uniforms are created ONCE (empty useMemo deps). All values —
 * time, audio, dose, reactivity, colors — are updated every frame in useFrame.
 * This eliminates the stale-closure / time-reset bug from v2 and ensures
 * every mode always moves and always reacts to audio.
 */

const FULLSCREEN_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = position.xy * 0.5 + 0.5;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// ── Uniform shell: created ONCE, all values updated per-frame ─────
function createUniforms(processor: AudioProcessor) {
  return {
    uTime: { value: 0 },
    uAudioTex: { value: processor.texture },
    uBass: { value: 0 },
    uSub: { value: 0 },
    uMid: { value: 0 },
    uTreble: { value: 0 },
    uAir: { value: 0 },
    uLevel: { value: 0 },
    uBeat: { value: 0 },
    uCentroid: { value: 0.5 },
    uFlux: { value: 0 },
    uDissonance: { value: 0 },
    uOceanic: { value: 0 },
    uRestructuring: { value: 0 },
    uVigilance: { value: 1 },
    uDose: { value: 0.6 },
    uReactivity: { value: 1.0 },
    uColorA: { value: new THREE.Vector3(1, 0.2, 0.9) },
    uColorB: { value: new THREE.Vector3(0.2, 0.9, 1) },
    uColorC: { value: new THREE.Vector3(1, 0.9, 0.2) },
    uResolution: { value: new THREE.Vector2(1, 1) },
  };
}

type U = ReturnType<typeof createUniforms>;

/** Push EVERYTHING into the uniform shell — called every frame. */
function syncUniforms(u: U, f: AudioFeatures, psy: PsyState, w: number, h: number, delta: number) {
  // Time — never resets
  u.uTime.value += delta;

  // Resolution
  u.uResolution.value.set(w, h);

  // Audio features
  u.uBass.value = f.bass;
  u.uSub.value = f.sub;
  u.uMid.value = f.mid;
  u.uTreble.value = f.treble;
  u.uAir.value = f.air;
  u.uLevel.value = f.level;
  u.uBeat.value = f.beatEnvelope;
  u.uCentroid.value = f.centroid;
  u.uFlux.value = f.flux;
  u.uDissonance.value = f.dissonance;
  u.uOceanic.value = f.oceanic;
  u.uRestructuring.value = f.restructuring;
  u.uVigilance.value = f.vigilance;

  // Psy state — dose, reactivity, palette colors
  u.uDose.value = psy.dose;
  u.uReactivity.value = psy.reactivity;
  const p = PALETTES[psy.paletteIndex] ?? PALETTES[0];
  u.uColorA.value.set(...p.a);
  u.uColorB.value.set(...p.b);
  u.uColorC.value.set(...p.c);
}

// ════════════════════════════════════════════════════════════════════
// MODE: TUNNEL
// ════════════════════════════════════════════════════════════════════
const TUNNEL_FRAG = /* glsl */ `
  ${CORTICAL_FIELD}
  ${SYMMETRY_FOLD}
  ${HYPERBOLIC_WARP}
  ${NOISE_FBM}
  ${AUDIO_SAMPLE}
  ${COLOR_LIB}
  uniform float uTime, uBass, uSub, uMid, uLevel, uBeat, uCentroid, uFlux;
  uniform float uDissonance, uOceanic, uRestructuring, uVigilance, uDose, uReactivity;
  uniform vec3 uColorA, uColorB, uColorC;
  uniform vec2 uResolution;
  varying vec2 vUv;

  void main() {
    vec2 uv = (vUv - 0.5);
    uv.x *= uResolution.x / uResolution.y;

    float k = (0.35 + uOceanic * 0.5) * (0.5 + uDose * 0.5);
    vec2 p = hyperbolicWarp(uv, k);
    float n = floor(3.0 + uDose * 8.0 + uRestructuring * 4.0);
    p = foldSymmetry(p, n);

    float travel = uTime * (0.8 + uBass * 3.0 + uBeat * 4.0);
    float breath = 1.0 + sin(uTime * 1.8) * 0.1 + uBeat * 0.15 + uBass * 0.2;
    p *= breath;

    vec4 w = vec4(1.0, 0.3 + uMid * 1.0, 0.2 + uRestructuring * 0.5, 0.15 + uDissonance * 0.3);
    float field = corticalField(p, travel, w);

    int oct = 3 + int(uDose * 3.0);
    float drift = warpedFbm(p * 3.0 + travel * 0.3, oct, 0.6 + uDose + uFlux * 1.5);
    field = mix(field, drift, 0.3 + uFlux * 0.4);

    float rings = pow(abs(field), 0.55);
    float r = length(uv);
    float portal = smoothstep(1.2, 0.05, r);

    vec3 col = mix(uColorA, uColorB, rings);
    col = mix(col, uColorC, smoothstep(0.35, 0.9, rings + uCentroid * 0.2));
    col += ascColor(vUv.x, uCentroid, uRestructuring, uVigilance, uTime) * 0.2;

    float hdr = 1.0 + uBass * 1.5 + uBeat * 2.0;
    col *= portal * (rings * 0.5 + 0.5) * hdr;

    float beatRing = smoothstep(0.12, 0.0, abs(r - 0.1 - fract(uTime * 0.4) * 0.25));
    col += beatRing * uBeat * vec3(1.0, 0.95, 0.9) * 1.5;

    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(col, vec3(lum) * vec3(0.5, 0.6, 1.0), uDissonance * 0.4);

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

function TunnelMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  useFrame((state, delta) => {
    const f = processor.update();
    syncUniforms(uniforms, f, psy, state.size.width, state.size.height, delta);
  });
  return <ScreenQuad><shaderMaterial vertexShader={FULLSCREEN_VERT} fragmentShader={TUNNEL_FRAG} uniforms={uniforms} /></ScreenQuad>;
}

// ════════════════════════════════════════════════════════════════════
// MODE: SPIRAL
// ════════════════════════════════════════════════════════════════════
const SPIRAL_FRAG = /* glsl */ `
  ${CORTICAL_FIELD}
  ${SYMMETRY_FOLD}
  ${HYPERBOLIC_WARP}
  ${NOISE_FBM}
  ${AUDIO_SAMPLE}
  ${COLOR_LIB}
  uniform float uTime, uBass, uMid, uLevel, uBeat, uCentroid, uFlux, uDissonance;
  uniform float uOceanic, uRestructuring, uVigilance, uDose, uReactivity;
  uniform vec3 uColorA, uColorB, uColorC;
  uniform vec2 uResolution;
  varying vec2 vUv;

  void main() {
    vec2 uv = (vUv - 0.5);
    uv.x *= uResolution.x / uResolution.y;
    float k = (0.2 + uOceanic * 0.3) * (0.5 + uDose * 0.5);
    vec2 p = hyperbolicWarp(uv, k);
    float n = floor(4.0 + uDose * 9.0 + uBeat * 2.0);
    p = foldSymmetry(p, n);

    float travel = uTime * (1.2 + uMid * 3.0 + uBeat * 4.0);
    vec4 w = vec4(0.2 + uBass * 0.3, 1.0, 0.3 + uRestructuring * 0.5, 0.2 + uDissonance * 0.2);
    float field = corticalField(p * (1.0 + uBass * 0.6), travel, w);

    int oct = 2 + int(uDose * 4.0);
    float drift = warpedFbm(p * 4.0 + travel * 0.25, oct, 0.5 + uDose + uFlux * 1.2);
    field = mix(field, drift, 0.3 + uFlux * 0.35);

    float vis = pow(abs(field), 0.45);

    vec3 col = mix(uColorB, uColorA, vis);
    col = mix(col, uColorC, smoothstep(0.4, 0.95, vis));
    col += ascColor(vUv.y, uCentroid, uRestructuring, uVigilance, uTime) * 0.2;

    float r = length(uv);
    float falloff = smoothstep(1.3, 0.05, r);
    float hdr = 1.0 + uMid * 2.0 + uBeat * 2.5;
    col *= falloff * (vis * 0.4 + 0.6) * hdr;

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

function SpiralMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  useFrame((state, delta) => {
    const f = processor.update();
    syncUniforms(uniforms, f, psy, state.size.width, state.size.height, delta);
  });
  return <ScreenQuad><shaderMaterial vertexShader={FULLSCREEN_VERT} fragmentShader={SPIRAL_FRAG} uniforms={uniforms} /></ScreenQuad>;
}

// ════════════════════════════════════════════════════════════════════
// MODE: WAVY FORM
// ════════════════════════════════════════════════════════════════════
const WAVY_FRAG = /* glsl */ `
  ${SDF_LIB}
  ${NOISE_FBM}
  ${AUDIO_SAMPLE}
  ${COLOR_LIB}
  uniform float uTime, uBass, uMid, uTreble, uLevel, uBeat, uCentroid, uFlux;
  uniform float uDissonance, uOceanic, uRestructuring, uVigilance, uDose, uReactivity;
  uniform vec3 uColorA, uColorB, uColorC;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float map(vec3 p) {
    float t = uTime * 0.8 + uBeat * 1.2;
    float c = cos(t * 0.5), s = sin(t * 0.5);
    p.xz = mat2(c, -s, s, c) * p.xz;
    p.y += sin(t * 0.9) * 0.12 + sin(t * 1.5 + p.x) * 0.06;

    float breath = 1.0 + sin(uTime * 2.0) * 0.08 + uBass * 0.7 + uBeat * 0.5;
    float d1 = sdSphere(p, 1.0 * breath);
    float d2 = sdTorus(p, vec2(0.85, 0.35 + uMid * 0.35));
    float d3 = sdBox(p, vec3(0.75 + uTreble * 0.35));
    float d4 = sdOctahedron(p, 0.9 + uLevel * 0.12);

    float m = fract(t * 0.25 + uRestructuring * 0.5);
    float k = 0.35 + uDose * 0.4;
    float d = smin(d1, mix(d2, mix(d3, d4, smoothstep(0.66, 1.0, m)), smoothstep(0.33, 0.66, m)), k);

    int oct = 3 + int(uDose * 3.0);
    float n = warpedFbm(p.xy * 2.5 + uTime * 0.4, oct, 0.7 + uDose + uFlux * 2.0) - 0.5;
    d += n * (0.2 + uFlux * 0.35 + uRestructuring * 0.2 + sin(uTime * 2.5) * 0.04);
    return d;
  }

  vec3 calcNormal(vec3 p) {
    vec2 e = vec2(0.002, 0.0);
    return normalize(vec3(map(p+e.xyy)-map(p-e.xyy), map(p+e.yxy)-map(p-e.yxy), map(p+e.yyx)-map(p-e.yyx)));
  }

  void main() {
    vec2 uv = (vUv - 0.5);
    uv.x *= uResolution.x / uResolution.y;
    float camAngle = uTime * 0.12;
    vec3 ro = vec3(sin(camAngle)*0.4, 0.1, 3.5);
    vec3 target = vec3(0.0, sin(uTime*0.15)*0.12, 0.0);
    vec3 fwd = normalize(target - ro);
    vec3 right = normalize(cross(fwd, vec3(0,1,0)));
    vec3 up = cross(right, fwd);
    vec3 rd = normalize(fwd*1.5 + right*uv.x + up*uv.y);

    float t = 0.0; bool hit = false;
    for (int i = 0; i < 72; i++) {
      vec3 p = ro + rd * t;
      float d = map(p);
      if (d < 0.003) { hit = true; break; }
      t += d * 0.75;
      if (t > 12.0) break;
    }

    vec3 col = vec3(0.03, 0.01, 0.05);
    if (hit) {
      vec3 p = ro + rd * t;
      vec3 n = calcNormal(p);
      vec3 ld = normalize(vec3(0.5+sin(uTime*0.25)*0.3, 0.7, 0.6+cos(uTime*0.35)*0.2));
      float diff = max(dot(n, ld), 0.0);
      float fres = pow(1.0 - max(dot(n, -rd), 0.0), 2.5);

      vec3 base = mix(uColorA, uColorB, diff);
      base = mix(base, uColorC, fres);
      vec3 asc = ascColor(vUv.x + uTime*0.015, uCentroid, uRestructuring, uVigilance, uTime);
      col = base * (0.4 + diff * 1.2) + fres * uColorC * 1.8 + asc * 0.25;
      col *= 1.0 + uBeat * 2.0 + fres * 1.5 + uBass * 0.8;
    } else {
      int oct = 2 + int(uDose * 2.0);
      float bg = warpedFbm(uv * 3.0 + uTime * 0.08, oct, 0.5);
      col = mix(vec3(0.03,0.01,0.05), uColorB * 0.2, bg * (0.3 + uOceanic * 0.4 + sin(uTime*0.4)*0.05));
    }
    col = mix(col, vec3(dot(col, vec3(0.299,0.587,0.114))) * vec3(0.5,0.6,1.0), uDissonance * 0.3);
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

function WavyFormMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  useFrame((state, delta) => {
    const f = processor.update();
    syncUniforms(uniforms, f, psy, state.size.width, state.size.height, delta);
  });
  return <ScreenQuad><shaderMaterial vertexShader={FULLSCREEN_VERT} fragmentShader={WAVY_FRAG} uniforms={uniforms} /></ScreenQuad>;
}

// ════════════════════════════════════════════════════════════════════
// MODE: LATTICE (vizz.fm mesh grid)
// ════════════════════════════════════════════════════════════════════
const LATTICE_VERT = /* glsl */ `
  ${NOISE_FBM}
  ${AUDIO_SAMPLE}
  uniform float uTime, uBass, uMid, uTreble, uLevel, uBeat, uDose, uReactivity;
  uniform sampler2D uAudioTex;
  varying float vH;
  varying vec2 vUv2;
  void main() {
    vUv2 = uv;
    vec3 pos = position;
    float band = sampleBand(uAudioTex, uv.x);
    float bandY = sampleBand(uAudioTex, uv.y);
    int oct = 3 + int(uDose * 3.0);
    float warp = 0.5 + uDose * 0.5 + uReactivity * 0.5;
    float h = warpedFbm(pos.xy * 0.5 + uTime * 0.2, oct, warp);
    float wave = sin(pos.x * 0.7 + uTime * 1.0) * cos(pos.y * 0.5 + uTime * 0.7) * 1.2;
    float disp = (h - 0.5) * (2.5 + uBass * 4.0 + uBeat * 6.0)
               + wave
               + band * 3.0 * uReactivity
               + bandY * 1.5 * uReactivity
               + uBeat * 3.0;
    pos.z += disp;
    vH = disp;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;
const LATTICE_FRAG = /* glsl */ `
  ${COLOR_LIB}
  uniform float uTime, uBass, uMid, uCentroid, uRestructuring, uVigilance, uBeat, uLevel;
  uniform vec3 uColorA, uColorB, uColorC;
  varying float vH;
  varying vec2 vUv2;
  void main() {
    float h = clamp(vH * 0.18 + 0.5, 0.0, 1.0);
    vec3 col = mix(uColorA, uColorB, h);
    col = mix(col, uColorC, smoothstep(0.4, 1.0, h));
    vec3 asc = ascColor(vUv2.x, uCentroid, uRestructuring, uVigilance, uTime);
    col = col * 0.5 + asc * 0.5;
    col *= (1.0 + h * 2.0 + uBeat * 2.5 + uLevel * 1.5);
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

function LatticeMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  const matRef = useRef<THREE.ShaderMaterial>(null!);
  const gridRef = useRef<THREE.Mesh>(null!);
  useFrame((state, delta) => {
    const f = processor.update();
    syncUniforms(uniforms, f, psy, state.size.width, state.size.height, delta);
    if (gridRef.current) {
      const t = state.clock.elapsedTime;
      gridRef.current.rotation.z = Math.sin(t * 0.12) * 0.15;
      gridRef.current.rotation.x = -1.15 + Math.sin(t * 0.08) * 0.06;
    }
  });
  return (
    <mesh ref={gridRef} rotation={[-1.15, 0, 0]} position={[0, 0.2, 0]}>
      <planeGeometry args={[26, 26, 120, 120]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={LATTICE_VERT}
        fragmentShader={LATTICE_FRAG}
        uniforms={uniforms}
        wireframe
        blending={THREE.AdditiveBlending}
        transparent
        depthWrite={false}
      />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODE: OCEANIC (boundless particle field)
// ════════════════════════════════════════════════════════════════════
const OCEANIC_COUNT = 40000;
const OCEANIC_VERT = /* glsl */ `
  ${NOISE_FBM}
  ${AUDIO_SAMPLE}
  uniform float uTime, uBass, uMid, uTreble, uLevel, uBeat, uOceanic, uDose, uReactivity;
  uniform sampler2D uAudioTex;
  attribute float aBand;
  varying float vFreq;
  varying float vDepth;
  void main() {
    float freq = sampleBand(uAudioTex, aBand);
    vFreq = freq;
    vec3 dir = normalize(position);
    float baseR = length(position);
    float breathe = sin(uTime * 0.4 + aBand * 6.28) * 1.2;
    float expand = breathe + uOceanic * 4.0 + uBass * 3.5 + uBeat * 4.0;
    int oct = 2 + int(uDose * 3.0);
    float scatter = warpedFbm(position.xy * 0.7 + uTime * 0.12, oct, 0.5 + uDose) - 0.5;
    float r = baseR + expand + scatter * (1.5 + uOceanic * 3.5 + uBeat * 2.0);
    vec3 pos = dir * r;
    float sp = uTime * (0.15 + aBand * 0.3);
    float c = cos(sp), s = sin(sp);
    pos.xz = mat2(c, -s, s, c) * pos.xz;
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = (2.5 + freq * 6.0 + uBeat * 7.0 + uLevel * 3.0) * (9.0 / -mv.z);
    vDepth = clamp((-mv.z - 2.0) / 10.0, 0.0, 1.0);
  }
`;
const OCEANIC_FRAG = /* glsl */ `
  ${COLOR_LIB}
  uniform float uTime, uOceanic, uCentroid, uRestructuring, uVigilance, uBeat, uLevel;
  uniform vec3 uColorA, uColorB, uColorC;
  varying float vFreq;
  varying float vDepth;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float alpha = smoothstep(0.5, 0.05, d);
    if (alpha < 0.01) discard;
    vec3 col = mix(uColorA, uColorB, vFreq);
    col = mix(col, uColorC, vDepth * 0.5 + uOceanic * 0.3);
    vec3 asc = ascColor(vFreq, uCentroid, uRestructuring, uVigilance, uTime);
    col = mix(col, asc, 0.25);
    col *= (1.0 + vFreq * 1.5 + uBeat * 2.5 + uLevel * 1.0);
    alpha *= (1.0 - vDepth * 0.35 - uOceanic * 0.25);
    gl_FragColor = vec4(max(col * alpha, 0.0), alpha);
  }
`;

function OceanicMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  const pointsRef = useRef<THREE.Points>(null!);

  const geometry = useMemo(() => {
    const positions = new Float32Array(OCEANIC_COUNT * 3);
    const bands = new Float32Array(OCEANIC_COUNT);
    for (let i = 0; i < OCEANIC_COUNT; i++) {
      const r = 3.0 + Math.random() * 3.0;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
      bands[i] = Math.pow(Math.random(), 1.3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('aBand', new THREE.BufferAttribute(bands, 1));
    return g;
  }, []);

  useFrame((state, delta) => {
    const f = processor.update();
    syncUniforms(uniforms, f, psy, state.size.width, state.size.height, delta);
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.08;
      pointsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.2;
    }
  });

  return (
    <points ref={pointsRef} geometry={geometry}>
      <shaderMaterial
        vertexShader={OCEANIC_VERT}
        fragmentShader={OCEANIC_FRAG}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// ── Camera rig ─────────────────────────────────────────────────────
function Rig() {
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    state.camera.position.x = Math.sin(t * 0.1) * 0.6;
    state.camera.position.y = Math.cos(t * 0.08) * 0.4;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

// ── Mode router ────────────────────────────────────────────────────
function ActiveMode({ processor, psy, uniforms }: { processor: AudioProcessor; psy: PsyState; uniforms: U }) {
  switch (psy.phenomenon) {
    case 'tunnel': return <TunnelMode processor={processor} psy={psy} uniforms={uniforms} />;
    case 'spiral': return <SpiralMode processor={processor} psy={psy} uniforms={uniforms} />;
    case 'wavyform': return <WavyFormMode processor={processor} psy={psy} uniforms={uniforms} />;
    case 'lattice': return <><LatticeMode processor={processor} psy={psy} uniforms={uniforms} /><Rig /></>;
    case 'oceanic': return <><OceanicMode processor={processor} psy={psy} uniforms={uniforms} /><Rig /></>;
    default: return <TunnelMode processor={processor} psy={psy} uniforms={uniforms} />;
  }
}

export function PsychedelicScene({
  processor,
  psy,
}: {
  processor: AudioProcessor;
  psy: PsyState;
}) {
  // Create uniforms ONCE — never recreated on psy change.
  // All values synced every frame in useFrame via syncUniforms.
  const uniforms = useMemo(() => createUniforms(processor), [processor]);
  const caOffset = useMemo(() => new THREE.Vector2(0.0015, 0.0015), []);

  return (
    <CanvasErrorBoundary fallback={<div className="w-full h-full bg-[#03020a]" />}>
    <Canvas
      camera={{ position: [0, 0, 8], fov: 60 }}
      dpr={[1, 2]}
      gl={{ alpha: false, antialias: false, powerPreference: 'high-performance' }}
      style={{ width: '100%', height: '100%' }}
    >
      <color attach="background" args={['#03020a']} />
      <ActiveMode processor={processor} psy={psy} uniforms={uniforms} />
      {psy.postFx && (
        <EffectComposer multisampling={0} frameBufferType={THREE.HalfFloatType}>
          <Bloom intensity={1.2} luminanceThreshold={0.2} luminanceSmoothing={0.4} radius={0.7} />
          <ChromaticAberration offset={caOffset} />
          <Noise premultiply blendFunction={BlendFunction.SCREEN} opacity={0.05} />
          <Vignette eskil={false} offset={0.3} darkness={0.85} />
        </EffectComposer>
      )}
    </Canvas>
    </CanvasErrorBoundary>
  );
}

export default PsychedelicScene;
